<template>
  <section class="block">
    <h1 v-if="organization.name !== ''">
      組織名: <span>{{ organization.name }}</span>
    </h1>
    <h1 v-else>組織名</h1>
    <Snackbar :option="snackbar"></Snackbar>
    <div class="form">
      <table>
        <tbody>
        <tr>
          <th>プロファイル名</th>
          <td>
            <input v-if="editName" type="text" class="field" v-model="organization.name" />
            <span v-else>{{ organization.name }}</span>
          </td>
        </tr>
        <tr>
          <th>説明</th>
          <td>
            <input type="text" class="field" v-model="organization.description" />
          </td>
        </tr>
        <tr>
          <th>設定</th>
          <td>
            <div class="checkbox">
              <label>
                <input type="checkbox" v-model="kintone_data" />
                <span class="ms-2">Kintone のデータ</span>
              </label>
            </div>
          </td>
        </tr>
        <tr v-if="kintone_data">
          <th>Kintoneドメイン</th>
          <td>
            <input v-if="editName" type="text" class="field" v-model="organization.kintone_url" />
            <span v-else>{{ organization.kintone_url }}</span>
          </td>
        </tr>
        <tr v-if="kintone_data">
          <th>Email</th>
          <td>
            <input v-if="editName" type="text" class="field" v-model="organization.kintone_email" />
            <span v-else>{{ organization.kintone_email }}</span>
          </td>
        </tr>
        <tr v-if="kintone_data">
          <th>パスワード</th>
          <td>
            <input v-if="editName" type="text" class="field" v-model="organization.kintone_password" />
            <span v-else>{{ organization.kintone_password }}</span>
          </td>
        </tr>
        <tr v-if="edit && organization.createdAt">
          <th>作成日時</th>
          <td>{{ new Date(organization.createdAt).toLocaleString() }}</td>
        </tr>
        <tr v-if="edit && organization.updatedAt">
          <th>更新日時</th>
          <td>{{ new Date(organization.updatedAt).toLocaleString() }}</td>
        </tr>
        </tbody>
      </table>
      <nav>
        <button class="btn btn-secondary" v-if="!edit" @click="add" :disabled="loading">
          {{ loading ? '処理中…' : '追加' }}
        </button>
        <button class="btn btn-secondary" v-if="edit" @click="update" :disabled="loading">
          {{ loading ? '処理中…' : '編集' }}
        </button>
        <button class="btn btn-danger" v-if="edit && editName && loggedInUser.role === AccountRole.SUPER_ADMIN"
                @click="remove">削除
        </button>
      </nav>
    </div>
  </section>
  <Dialog ref="remove" class="dialog">
    <h4 class="title">Confirm</h4>
    <div class="message">
      <p class="text">Do you really remove this organization?</p>
    </div>
    <div class="action">
      <button class="btn btn-light" @click="removeCancel">Cancel</button>
      <button class="btn btn-danger" @click="removeConfirmed">削除</button>
    </div>
  </Dialog>
  <Dialog ref="error" class="dialog">
    <h4 class="title">Error</h4>
    <div class="message">
      <p class="text">{{ error.message }}</p>
    </div>
    <div class="action">
      <button class="btn btn-light" @click="errorClose">Close</button>
    </div>
  </Dialog>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import Dialog from '@/components/Dialog.vue';
import Snackbar from '@/components/Snackbar.vue';
import { Organization, OrganizationData } from '@/lib/organization';
import router from '@/router';
import { Account, AccountData, AccountRole } from '@/lib/account';

export default defineComponent({
  name: 'OrganizationEditor',
  components: {
    Dialog,
    Snackbar,
  },
  props: {
    edit: Boolean,
    orgSlug: String,
  },
  data() {
    return {
      loggedInUser: {
        slug: '',
        email: '',
        first_name: '',
        last_name: '',
        password: '',
        role: 0,
        created: undefined,
        updated: undefined,
      } as AccountData,
      organization: {
        name: '',
        description: '',
        slug: '',
        kintone_url: '',
        kintone_email: '',
        kintone_password: '',
        createdAt: undefined,
        updatedAt: undefined,
      } as OrganizationData,
      snackbar: {
        type: '',
        message: '',
      },
      editName: false,
      error: {
        message: '',
        back: false,
      },
      AccountRole,
      kintone_data: false,
      loading: false,
    };
  },
  async mounted() {
    try {
      this.loggedInUser = await new Account().getByToken();
      this.editName = true;
    } catch (e) {
      console.info(e);
    }

    if (this.orgSlug) {
      try {
        const org = new Organization(this.orgSlug);
        const data = await org.get();
        if (data) {
          this.organization = data;
          this.kintone_data = !!(data.kintone_url || data.kintone_email || data.kintone_password);
        } else {
          this.error.message = 'This organization was not found';
          this.error.back = true;
          (this.$refs.error as InstanceType<typeof Dialog>)?.open();
        }
      } catch (e) {
        if (e instanceof Error) {
          this.error.message = e.message;
          this.error.back = true;
          (this.$refs.error as InstanceType<typeof Dialog>)?.open();
        }
        console.warn(e);
      }
    }
  },
  methods: {
    normalize(url: string): string {
      if (!url) return '';
      if (/^https?:\/\//i.test(url)) {
        return url;
      }
      return `https://${url}`;
    },
    validate(): boolean {
      const showWarning = (message: string) => {
        this.snackbar.type = 'warning';
        this.snackbar.message = message;
        return false;
      };

      if (!this.organization.name) return showWarning('組織名を空にすることはできません。');
      if (!this.organization.description) return showWarning('組織の説明を空にすることはできません。');

      if (this.kintone_data) {
        const { kintone_url, kintone_email, kintone_password } = this.organization;

        if (!kintone_url) return showWarning('Kintoneドメインを空にすることはできません。');
        if (!kintone_email) return showWarning('Kintoneメールを空にすることはできません。');

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(kintone_email)) return showWarning('Kintoneメールの形式が正しくありません。');

        if (!this.edit && !kintone_password) return showWarning('Kintoneパスワードを空にすることはできません。');

        this.organization.kintone_url = this.normalize(kintone_url);
        this.organization.kintone_url = this.organization.kintone_url ? new URL(this.organization.kintone_url).origin : '';

        const domainRegex = /^(https?:\/\/)?([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}$/;
        if (!domainRegex.test(this.organization.kintone_url!)) return showWarning('Kintoneドメインの形式が正しくありません。');
      }

      this.snackbar.message = '';
      return true;
      },

      async add() {
        if (!this.validate()) {
          return;
        }
        try {
          const org = new Organization();
          if (!this.kintone_data) {
            this.organization.kintone_url = '';
            this.organization.kintone_email = '';
            this.organization.kintone_password = '';
          }
          this.loading = true;
          const dataRes = await org.add(this.organization as OrganizationData) as any;
          const slug = dataRes?.data?.slug;

           if (slug) {
              this.loading = false;
              router.replace(`/organizations/${slug}`);
            } else {
              this.loading = false;
              this.snackbar.type = 'warning';
              this.snackbar.message = dataRes?.message || '組織の作成に失敗しました。';
            }
        } catch (e) {
          this.loading = false;
          if (e instanceof Error) {
            this.error.message = e.message;
            (this.$refs.error as InstanceType<typeof Dialog>)?.open();
          }
          console.warn(e);
        }
    },
    async update() {
      if (!this.validate()) {
        return;
      }
      try {
        const org = new Organization(this.orgSlug);
        if (!this.kintone_data) {
          this.organization.kintone_url = '';
          this.organization.kintone_email = '';
          this.organization.kintone_password = '';
        }
        this.loading = true;
        const dataRes = await org.update(this.organization as OrganizationData) as any;
        const slug = dataRes?.data?.slug;
        if (slug) {
          this.loading = false;
          this.snackbar.type = 'success';
          this.snackbar.message = '組織が更新されました！';
        } else {
            this.loading = false;
            this.snackbar.type = 'warning';
            this.snackbar.message = dataRes?.message || '組織の作成に失敗しました。';
        }
      } catch (e) {
        this.loading = false;
        if (e instanceof Error) {
          this.error.message = e.message;
          (this.$refs.error as InstanceType<typeof Dialog>)?.open();
        }
        console.warn(e);
      }
    },
    remove() {
      (this.$refs.remove as InstanceType<typeof Dialog>)?.open();
    },
    async removeConfirmed() {
      (this.$refs.remove as InstanceType<typeof Dialog>)?.close();
      try {
        const org = new Organization(this.orgSlug);
        if (await org.delete()) {
          router.push('/');
        } else {
          this.snackbar.type = 'warning';
          this.snackbar.message = 'この組織の削除に失敗しました！';
        }
      } catch (e) {
        if (e instanceof Error) {
          this.error.message = e.message;
          (this.$refs.error as InstanceType<typeof Dialog>)?.open();
        }
        console.warn(e);
      }
    },
    removeCancel() {
      (this.$refs.remove as InstanceType<typeof Dialog>)?.close();
    },
    errorClose() {
      (this.$refs.error as InstanceType<typeof Dialog>)?.close();
      this.error.back && this.$router.back();
    },
  },
});
</script>

<style lang="scss" scoped>
@use '@/assets/const.scss' as *;
@use '@/assets/dialog.scss' as *;

.block {
  .form {
    margin: 0 10px;

    @media (min-width: $tablet) {
      margin: 0 20px;
    }

    @media (min-width: $desktop) {
      margin: 0 40px;
    }

    table {
      width: 100%;
      max-width: 640px;
    }

    th {
      padding: 5px;
      font-weight: normal;
      width: 30%;
    }

    td {
      padding: 5px;
       width: 70%;
    }

    .field {
      width: 100%;
    }

    nav {
      display: flex;
      margin-top: 20px;

      button {
        margin-right: 20px;

        &:last-child:not(:first-child) {
          margin-left: auto;
          margin-right: 0;
        }
      }
    }
  }
}

.dialog {
  @include dialog();
}
</style>